import { makeAutoObservable } from "mobx";
import { isTMessage, TFunction, TMessage } from "../../i18n/tt";
import { FilePreviewProps } from "../FilePreview";
import { downloadFile } from "../../api";

export type FileToPreview = {
  name?: string;
  reference: string;
};

export class BatchPreviewModel {
  title: TMessage | string;
  files: FileToPreview[];
  selectedFileIndex = 0;

  state: { type: "loading" } | { type: "ready" } | { type: "error" } = {
    type: "loading",
  };

  constructor(
    title: TMessage | string,
    readonly filePreview: (file: FileToPreview) => FilePreviewProps,
    files?: FileToPreview[],
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.title = title;
    this.files = files ?? [];
    if (files !== undefined) {
      this.state = { type: "ready" };
    }
  }

  setFiles(files: FileToPreview[]) {
    this.files = files;
    this.state = { type: "ready" };
  }

  setError() {
    this.state = { type: "error" };
  }

  get numberOfFiles() {
    return this.files.length;
  }

  get selectedFile() {
    return this.numberOfFiles > 0 ? this.files[this.selectedFileIndex] : undefined;
  }

  get file(): FilePreviewProps | undefined {
    return this.selectedFile ? this.filePreview(this.selectedFile) : undefined;
  }

  get showNavigationButtons() {
    return this.numberOfFiles > 1;
  }

  selectFile(file: FileToPreview) {
    this.selectedFileIndex = this.files.indexOf(file) ?? 0;
  }

  nextDocument() {
    if (this.selectedFileIndex < this.numberOfFiles - 1) {
      this.selectedFileIndex++;
    }
  }

  previousDocument() {
    if (this.selectedFileIndex > 0) {
      this.selectedFileIndex--;
    }
  }

  get canHandleDownload() {
    return (
      this.numberOfFiles > 0 && this.file?.filename !== undefined && this.file?.file !== undefined
    );
  }

  handleDownload(t: TFunction) {
    if (this.file?.file !== undefined) {
      const filename =
        this.file && isTMessage(this.file.filename) ? t(this.file.filename) : undefined;
      downloadFile(this.file.file, filename);
    }
  }
}

export const toFileToPreview = (name: string, reference: string): FileToPreview => {
  return { name, reference };
};
